/**
 * ============================================================================
 * Bold Aviator — Launch splash countdown
 * ----------------------------------------------------------------------------
 * TEMPORARY launch furniture — see docs/launch-splash.md.
 *
 * WHICH PAGE a visitor gets is decided server-side in Liquid and is not this
 * script's business. This only drives the ticking digits and the self-reload at
 * zero. For that it wants a clock that is both AUTHORITATIVE and FRESH, and the
 * two obvious sources each fail one of those:
 *
 *   · The timestamp Liquid stamped into the page is authoritative but can be
 *     stale — Shopify's docs say `{{ 'now' | date }}` "might not be updated for
 *     every page view, depending on the context and caching".
 *   · The browser clock is always fresh but is whatever the visitor set it to.
 *
 * So on load we ask the server what time it is for real: a cache-busted HEAD
 * whose `Date` response header is Shopify's own clock, seconds old. That is the
 * source of truth once it lands. The other two only cover the gap before it
 * arrives, or if it never does.
 * ============================================================================
 */
(function () {
  var root = document.querySelector('[data-launch-splash]');
  if (!root) return;

  var launchTs = parseInt(root.getAttribute('data-launch-at'), 10);
  var stampedTs = parseInt(root.getAttribute('data-server-now'), 10);
  if (!launchTs) return;

  var RELOAD_KEY = 'ba_launch_reloads';
  var MAX_RELOADS = 2;
  var SKEW_TOLERANCE = 120;

  function perfNow() {
    return (window.performance && performance.now()) || 0;
  }

  function clientSeconds() {
    return Math.floor(Date.now() / 1000);
  }

  /* When anchored, time is read as "the server said X at monotonic mark M",
     advanced by a clock the visitor's system settings can't move. */
  var anchor = null;

  function anchorTo(serverSeconds) {
    anchor = { at: serverSeconds, mark: perfNow() };
  }

  function nowSeconds() {
    if (!anchor) return clientSeconds();
    return Math.floor(anchor.at + (perfNow() - anchor.mark) / 1000);
  }

  /* Before the live reading lands, fall back to the stamped timestamp — but only
     when it reads LATER than the browser, which staleness cannot cause and so
     can only mean the browser clock is the wrong one. A stamped time that reads
     earlier is exactly what a cached page looks like, and trusting it would let
     stale HTML hold the site dark. */
  if (stampedTs && stampedTs - clientSeconds() > SKEW_TOLERANCE) {
    anchorTo(stampedTs);
  }

  /* Shopify's clock, straight off the wire. `cache: 'no-store'` plus a unique
     query string keeps any cache from answering; `Age` is added back because an
     intermediary that does answer reports the response's original `Date` and how
     long it has been sitting. */
  function syncWithServer() {
    if (!window.fetch) return Promise.resolve();

    var url = window.location.pathname + '?_clock=' + Date.now();

    return fetch(url, { method: 'HEAD', cache: 'no-store', credentials: 'omit' })
      .then(function (response) {
        var date = response.headers.get('Date');
        if (!date) return;

        var seconds = Math.floor(new Date(date).getTime() / 1000);
        if (!seconds) return;

        var age = parseInt(response.headers.get('Age'), 10);
        if (age > 0) seconds += age;

        anchorTo(seconds);
        if (timer) tick();
      })
      .catch(function () {
        /* Offline or blocked — the fallbacks above already have us covered. */
      });
  }

  var cells = {
    days: root.querySelector('[data-unit="days"]'),
    hours: root.querySelector('[data-unit="hours"]'),
    minutes: root.querySelector('[data-unit="minutes"]'),
    seconds: root.querySelector('[data-unit="seconds"]')
  };
  var liveRegion = root.querySelector('[data-launch-live]');
  var clock = root.querySelector('[data-launch-clock]');
  var pending = root.querySelectorAll('[data-launch-pending]');
  var wheelsUp = root.querySelector('[data-launch-wheels-up]');
  var timer = null;

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function setCell(cell, value) {
    if (!cell) return;
    var text = pad(value);
    if (cell.textContent !== text) cell.textContent = text;
  }

  /* Reload once the clock hits zero so the visitor lands on the real site
     without touching anything. The cache-buster matters: a unique query string
     is what stops Shopify handing us back the same cached splash HTML. Capped
     via sessionStorage so a mis-set launch time can never cause a reload loop —
     if we've already tried twice, we stop and leave "Wheels up" on screen. */
  function goLive() {
    if (timer) { clearInterval(timer); timer = null; }

    if (clock) clock.hidden = true;
    for (var i = 0; i < pending.length; i++) pending[i].hidden = true;
    if (wheelsUp) wheelsUp.hidden = false;
    if (liveRegion) liveRegion.textContent = 'The store is opening now.';

    var tries = 0;
    try { tries = parseInt(sessionStorage.getItem(RELOAD_KEY), 10) || 0; } catch (e) {}
    if (tries >= MAX_RELOADS) return;
    try { sessionStorage.setItem(RELOAD_KEY, String(tries + 1)); } catch (e) {}

    var url = new URL(window.location.href);
    url.searchParams.set('t', String(nowSeconds()));
    window.setTimeout(function () { window.location.replace(url.toString()); }, 400);
  }

  function tick() {
    var remaining = launchTs - nowSeconds();

    if (remaining <= 0) {
      setCell(cells.days, 0);
      setCell(cells.hours, 0);
      setCell(cells.minutes, 0);
      setCell(cells.seconds, 0);
      goLive();
      return;
    }

    var days = Math.floor(remaining / 86400);
    var hours = Math.floor((remaining % 86400) / 3600);
    var minutes = Math.floor((remaining % 3600) / 60);
    var seconds = remaining % 60;

    /* Under a day the days cell is noise — drop it and let the row rebalance. */
    if (cells.days) {
      var daysCell = cells.days.closest('[data-launch-cell]');
      if (daysCell) daysCell.hidden = days === 0;
    }

    setCell(cells.days, days);
    setCell(cells.hours, hours);
    setCell(cells.minutes, minutes);
    setCell(cells.seconds, seconds);

    /* Screen readers get a coarse update once a minute rather than a
       once-a-second stream of digits. */
    if (liveRegion && seconds === 0) {
      liveRegion.textContent =
        'Back online in ' +
        (days ? days + ' days, ' : '') +
        hours + ' hours and ' + minutes + ' minutes.';
    }
  }

  function startTicking() {
    if (timer) return;
    tick();
    timer = window.setInterval(tick, 1000);
  }

  /* Hold the very first tick until Shopify's clock lands, or 1.5s, whichever
     comes first. This costs nothing visually — the digits in the HTML were
     rendered server-side and are already right — and it buys the one thing that
     matters: a browser clock running fast can't fire the go-live reload before
     we've had a chance to check it against the server. */
  var grace = new Promise(function (resolve) { window.setTimeout(resolve, 1500); });
  Promise.race([syncWithServer(), grace]).then(startTicking, startTicking);

  /* A backgrounded tab gets its timers throttled and may have been asleep for
     hours, so re-read the server clock rather than trusting the drift. */
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) return;
    tick();
    syncWithServer();
  });
})();
